import React from "react";
import PropTypes from "prop-types";
import {
  setColourShadeIfMissing,
  SIGNOL_COLOUR_NAMES,
} from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";

const Loader = ({
  size = "md",
  text = "",
  textColor = "white",
  inline = false,
  /** either primary or white */
  mode = "primary",
  /** either spinner (ring) or dots (three-dot wave) */
  variant = "spinner",
}) => {
  const textColorClass = `text-${setColourShadeIfMissing(textColor, 600)}`;
  const sizeMap = {
    xs: "h-4 w-4",
    sm: "h-5 w-5", // 1.25rem (20px)
    md: "h-8 w-8", // 2rem (32px)
    lg: "h-12 w-12", // 3rem (48px)
    xl: "h-16 w-16", // 4rem (64px)
    "2xl": "h-20 w-20", // 5rem (80px)
  };
  const paddingMap = {
    xs: "p-1",
    sm: "p-2",
    md: "p-3",
    lg: "p-4",
    xl: "p-6",
    "2xl": "p-8",
  };

  const extraClass = inline ? "inline-block align-middle" : "block";

  const elemSpinner = (
    <span
      className={`loader ease-linear rounded-full m-auto border-4 cursor-progress
        ${mode === "primary" ? "border-primary-200 border-t-primary-600" : "border-transparent border-t-white"}
        ${sizeMap[size]}
        ${extraClass}
      `}
    ></span>
  );
  const dotColorClass = mode === "primary" ? "bg-primary-600" : "bg-white";
  const elemDots = (
    <span
      className={`flex items-end justify-center gap-1.5 cursor-progress ${extraClass}`}
    >
      <span
        className={`inline-block rounded-full w-[5px] h-[5px] animate-waveDot ${dotColorClass}`}
      ></span>
      <span
        className={`inline-block rounded-full w-[5px] h-[5px] animate-waveDot [animation-delay:0.15s] ${dotColorClass}`}
      ></span>
      <span
        className={`inline-block rounded-full w-[5px] h-[5px] animate-waveDot [animation-delay:0.3s] ${dotColorClass}`}
      ></span>
    </span>
  );
  const elemText = text ? (
    <span className={`font-bold text-sm p-2 ${textColorClass} ${extraClass}`}>
      {text}
    </span>
  ) : (
    <></>
  );

  const elemIndicator = variant === "dots" ? elemDots : elemSpinner;

  if (inline) {
    return (
      <span>
        {elemIndicator} {elemText}
      </span>
    );
  }
  return (
    <label className={`flex flex-col items-center ${paddingMap[size]}`}>
      <div>{elemIndicator}</div>
      {text && <div>{elemText}</div>}
    </label>
  );
};

export const loaderControls = {
  size: {
    propType: PropTypes.string,
    controlType: "select",
    options: ["sm", "md", "lg", "xl", "2xl"],
    description: "Size of the loader",
  },
  text: {
    propType: PropTypes.string,
    controlType: "text",
    description: "Text to display below the loader",
  },
  textColor: {
    propType: PropTypes.string,
    controlType: "select",
    options: SIGNOL_COLOUR_NAMES,
    description: "Text color of the loader",
  },
  mode: {
    propType: PropTypes.oneOf(["primary", "white"]),
    controlType: "select",
    options: ["primary", "white"],
    defaultValue: "primary",
  },
  variant: {
    propType: PropTypes.oneOf(["spinner", "dots"]),
    controlType: "select",
    options: ["spinner", "dots"],
    description: "spinner (ring) or dots (three-dot wave, 5px–15px)",
    defaultValue: "spinner",
  },
};

Loader.propTypes = AppUtils.getPropTypeFromControls(loaderControls);

export default Loader;
